
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Task, Quadrant, SubTask, AccountabilityPartner, Category, TaskStatus, User, SystemEvent } from '../types';
import { geminiService } from '../services/geminiService';
import { firebaseService } from '../services/firebaseService';
import {
  Mic, X, Loader2, Sparkles, Plus, Trash2, ListChecks, Users,
  CheckCircle2, Circle, Settings2, Calendar, Zap, Layers,
  Mail, BellRing, Archive, ArrowUpRight, ExternalLink, Lock
} from 'lucide-react';
import { UpgradeModal } from './UpgradeModal';

interface TaskModalProps {
  task?: Task;
  user: User | null;
  onClose: () => void;
  onSave: (task: Partial<Task>) => void;
  initialQuadrant?: Quadrant;
  categories: Category[];
  onCategoryCreated: (cat: Category) => void;
  isPremium: boolean;
  onUpgrade: (plan: 'monthly' | 'yearly') => Promise<void>;
  onPromoteSubtask?: (task: Task, subtaskId: string) => void;
  onNavigateToTask?: (taskId: string) => void;
}

const PREMIUM_QUADRANTS = [Quadrant.DELEGATE, Quadrant.ELIMINATE];

const QUADRANT_OPTIONS = [
  { id: Quadrant.DO, label: 'Do First (Urgent & Important)' },
  { id: Quadrant.SCHEDULE, label: 'Schedule (Important)' },
  { id: Quadrant.DELEGATE, label: 'Delegate (Urgent)' },
  { id: Quadrant.ELIMINATE, label: 'Postpone' },
];

const PARTNER_TRIGGER_OPTIONS: { id: SystemEvent; label: string }[] = [
  { id: 'created', label: 'At Creation' },
  { id: 'subtask', label: 'Subtask Done' },
  { id: 'completed', label: 'Task Done' },
  { id: 'daily', label: 'Daily Pulse' }
];

export const TaskModal: React.FC<TaskModalProps> = ({ task, user, onClose, onSave, initialQuadrant, categories, onCategoryCreated, isPremium, onUpgrade, onPromoteSubtask, onNavigateToTask }) => {
  const [title, setTitle] = useState(task?.title || '');
  const [currentQuadrant, setCurrentQuadrant] = useState(task?.currentQuadrant || initialQuadrant || Quadrant.DO);
  const [description, setDescription] = useState(task?.description || '');
  const [status, setStatus] = useState<TaskStatus>(task?.status || 'NEW');
  const [category, setCategory] = useState(task?.category || 'primary_sector');
  const [dueDate, setDueDate] = useState<number | undefined>(task?.dueDate);

  const [subtasks, setSubtasks] = useState<SubTask[]>(() => (task?.subtasks || []).map(s => ({ ...s, id: s.id || Math.random().toString(36).substr(2, 9), completed: !!s.completed })));
  const [partners, setPartners] = useState<AccountabilityPartner[]>(task?.partners || []);
  const [newSubtask, setNewSubtask] = useState('');

  const [isAddingPartner, setIsAddingPartner] = useState(false);
  const [partnerEmail, setPartnerEmail] = useState('');
  const [partnerEvents, setPartnerEvents] = useState<SystemEvent[]>(['completed', 'subtask', 'daily']);

  const [isRecording, setIsRecording] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isBreakingDown, setIsBreakingDown] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#6366f1');
  const [isCreatingCat, setIsCreatingCat] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');

  const recognitionRef = useRef<any>(null);

  // Sync promoted status from parent task updates without overwriting local edits
  useEffect(() => {
    if (!task) return;
    setSubtasks(prev => prev.map(p => {
      const server = task.subtasks?.find(s => s.id === p.id);
      if (server && server.promoted !== p.promoted) {
        return { ...p, promoted: server.promoted, promotedTaskId: server.promotedTaskId, promotedAt: server.promotedAt };
      }
      return p;
    }));
  }, [task]);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.onresult = async (event: any) => {
        const transcript = event.results[0][0].transcript;
        setIsAiLoading(true);
        try {
          const extracted = await geminiService.processVoiceToTask(transcript);
          setTitle(extracted.title);
          setCurrentQuadrant(extracted.quadrant);
          if (extracted.description) setDescription(extracted.description);
        } finally {
          setIsRecording(false);
          setIsAiLoading(false);
        }
      };
      recognitionRef.current.onend = () => setIsRecording(false);
    }
  }, []);

  const handleVoiceToggle = () => {
    if (!isPremium) { setUpgradeFeature('AI Voice-to-Task'); setShowUpgradeModal(true); return; }
    if (isRecording) recognitionRef.current.stop();
    else { setIsRecording(true); recognitionRef.current.start(); }
  };

  const handleAiBreakdown = async () => {
    if (!title.trim()) return alert("Please add a title first.");
    if (!isPremium) { setUpgradeFeature('AI Task Decomposition'); setShowUpgradeModal(true); return; }
    setIsBreakingDown(true);
    try {
      const suggestions = await geminiService.suggestSubtasks(title, description || undefined);
      setSubtasks(prev => [...prev, ...suggestions.map(text => ({ id: Math.random().toString(36).substr(2, 9), text, completed: false }))]);
    } catch (error) {
      alert("AI service unavailable.");
    } finally {
      setIsBreakingDown(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCatName.trim() || !user) return;
    setIsCreatingCat(true);
    try {
      const newCat: Category = { id: Math.random().toString(36).substr(2, 9), userId: user.id, name: newCatName, color: newCatColor };
      const created = await firebaseService.createCategory(newCat);
      onCategoryCreated(created);
      setCategory(created.id);
      setIsAddingCategory(false);
      setNewCatName('');
    } finally { setIsCreatingCat(false); }
  };

  const handleAddPartner = () => {
    if (!partnerEmail.includes('@')) return alert("Invalid email.");
    setPartners([...partners, { email: partnerEmail, notifyOn: partnerEvents }]);
    setPartnerEmail('');
    setPartnerEvents(['completed', 'subtask', 'daily']); // Reset to defaults
    setIsAddingPartner(false);
  };

  const handleAddSubtask = () => {
    if (newSubtask.trim()) {
      setSubtasks([...subtasks, { id: Math.random().toString(36).substr(2, 9), text: newSubtask.trim(), completed: false }]);
      setNewSubtask('');
    }
  };

  const handleSubtaskPromote = (s: SubTask) => {
    if (task && onPromoteSubtask && !s.promoted) {
      onPromoteSubtask(task, s.id);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{task ? 'Edit Task' : 'New Task'}</h2>
            {task?.originType === 'promoted_subtask' && task.parentTaskId && (
              <button
                onClick={() => onNavigateToTask?.(task.parentTaskId!)}
                className="text-[10px] text-blue-600 font-bold flex items-center gap-1 hover:underline mt-1"
              >
                <ArrowUpRight size={10} /> Promoted from Parent Task
              </button>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500"><X size={20} /></button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8 custom-scrollbar">

          {/* Main Input */}
          <div className="space-y-4">
            <div className="relative">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What needs to be done?"
                className="w-full text-2xl font-semibold placeholder:text-slate-300 border-none focus:ring-0 p-0 text-slate-900 bg-transparent"
                autoFocus
              />
              <button
                onClick={handleVoiceToggle}
                className={`absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all ${isRecording ? 'bg-red-50 text-red-500' : 'text-slate-400 hover:text-blue-600'}`}
              >
                {isAiLoading ? <Loader2 size={20} className="animate-spin" /> : <Mic size={20} />}
              </button>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add description..."
              className="w-full text-sm text-slate-600 placeholder:text-slate-300 border-none focus:ring-0 p-0 bg-transparent resize-none h-20"
            />
          </div>

          {/* Properties Grid */}
          <div className="grid grid-cols-2 gap-6 p-5 bg-slate-50 rounded-xl border border-slate-100">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-500 flex items-center gap-1.5"><Zap size={12} /> Quadrant</label>
              <select
                value={currentQuadrant}
                onChange={(e) => {
                  const q = e.target.value as Quadrant;
                  if (!isPremium && PREMIUM_QUADRANTS.includes(q)) {
                    setUpgradeFeature(QUADRANT_OPTIONS.find(o => o.id === q)?.label || 'This quadrant');
                    setShowUpgradeModal(true);
                    return;
                  }
                  setCurrentQuadrant(q);
                }}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:border-blue-500"
              >
                {QUADRANT_OPTIONS.map(q => (
                  <option key={q.id} value={q.id} disabled={!isPremium && PREMIUM_QUADRANTS.includes(q.id)}>
                    {q.label}{!isPremium && PREMIUM_QUADRANTS.includes(q.id) ? ' 🔒' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-500 flex items-center gap-1.5"><Layers size={12} /> Category</label>
              {isAddingCategory ? (
                <div className="flex gap-2">
                  <input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="New Category" className="flex-1 bg-white border border-slate-200 rounded-lg px-2 text-sm" />
                  <input type="color" value={newCatColor} onChange={(e) => setNewCatColor(e.target.value)} className="w-8 h-9 border-none bg-transparent" />
                  <button onClick={handleCreateCategory} className="text-xs font-bold text-blue-600">Add</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className="flex-1 min-w-0 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:border-blue-500">
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                  <button onClick={() => setIsAddingCategory(true)} className="p-2 bg-white border border-slate-200 rounded-lg text-slate-500 hover:text-blue-600"><Plus size={16} /></button>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-500 flex items-center gap-1.5"><Settings2 size={12} /> Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:border-blue-500">
                <option value="NEW">New</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-500 flex items-center gap-1.5"><Calendar size={12} /> Due Date</label>
              <input
                type="date"
                value={dueDate ? new Date(dueDate).toISOString().split('T')[0] : ''}
                onChange={(e) => setDueDate(e.target.value ? new Date(e.target.value).getTime() : undefined)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Subtasks */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><ListChecks size={16} /> Checklist</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAiBreakdown}
                  disabled={isBreakingDown || !title.trim()}
                  className="text-xs font-medium text-indigo-600 flex items-center gap-1 hover:text-indigo-700 disabled:opacity-50"
                >
                  {isBreakingDown ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI Breakdown
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {subtasks.map(s => (
                <div key={s.id} className={`flex items-start gap-3 p-2 group rounded-lg ${s.promoted ? 'bg-slate-50 border border-slate-100' : ''}`}>
                  <button
                    onClick={() => !s.promoted && setSubtasks(prev => prev.map(st => st.id === s.id ? { ...st, completed: !st.completed } : st))}
                    className={`mt-0.5 shrink-0 ${s.completed ? 'text-emerald-500' : s.promoted ? 'text-slate-300 cursor-default' : 'text-slate-300 hover:text-slate-400'} transition-colors`}
                    disabled={!!s.promoted}
                  >
                    {s.completed ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                  </button>

                  {s.promoted ? (
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-sm text-slate-400 italic line-through decoration-slate-300">{s.text}</span>
                      <button
                        onClick={() => s.promotedTaskId && onNavigateToTask?.(s.promotedTaskId)}
                        className="flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded-md"
                      >
                        Promoted <ExternalLink size={10} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={s.text}
                        onChange={(e) => setSubtasks(prev => prev.map(st => st.id === s.id ? { ...st, text: e.target.value } : st))}
                        rows={1}
                        onInput={(e) => { const t = e.target as HTMLTextAreaElement; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px'; }}
                        ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                        className={`flex-1 bg-transparent text-sm border-none p-0 focus:ring-0 resize-none overflow-hidden ${s.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}
                      />
                      <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        {task && onPromoteSubtask && (
                          <button
                            onClick={() => handleSubtaskPromote(s)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Promote to Task"
                          >
                            <ArrowUpRight size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => setSubtasks(subtasks.filter(st => st.id !== s.id))}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-dashed border-slate-300 rounded-lg px-3 py-2.5 focus-within:border-indigo-400 focus-within:bg-white transition-all">
                  <Plus size={16} className="text-slate-400 shrink-0" />
                  <input
                    value={newSubtask}
                    onChange={(e) => setNewSubtask(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddSubtask(); }}
                    placeholder="Add your own step..."
                    className="flex-1 bg-transparent text-sm border-none p-0 focus:ring-0 placeholder:text-slate-400"
                  />
                </div>
                {newSubtask.trim() && (
                  <button
                    onClick={handleAddSubtask}
                    className="px-3 py-2.5 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-500 transition-all shrink-0"
                  >
                    Add
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Partners */}
          <div className="space-y-3 border-t border-slate-100 pt-6">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Users size={16} /> Accountability</h3>
              <button onClick={() => setIsAddingPartner(!isAddingPartner)} className="text-xs font-medium text-blue-600 hover:text-blue-700">
                {isAddingPartner ? 'Cancel' : '+ Add Partner'}
              </button>
            </div>

            {isAddingPartner && (
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <input
                  value={partnerEmail}
                  onChange={(e) => setPartnerEmail(e.target.value)}
                  placeholder="Partner email address"
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />

                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Notification Triggers</p>
                  <div className="grid grid-cols-2 gap-2">
                    {PARTNER_TRIGGER_OPTIONS.map(opt => (
                      <label key={opt.id} className="flex items-center gap-2 cursor-pointer p-2 bg-white border border-slate-200 rounded-lg hover:border-blue-400 transition-colors">
                        <input
                          type="checkbox"
                          checked={partnerEvents.includes(opt.id)}
                          onChange={(e) => {
                            if (e.target.checked) setPartnerEvents(prev => [...prev, opt.id]);
                            else setPartnerEvents(prev => prev.filter(id => id !== opt.id));
                          }}
                          className="w-3.5 h-3.5 rounded text-blue-600 border-slate-300 focus:ring-0"
                        />
                        <span className="text-[10px] font-bold text-slate-600">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <button onClick={handleAddPartner} className="w-full py-2 bg-blue-600 text-white rounded-lg text-xs font-bold shadow-sm hover:bg-blue-700 transition-all">Send Invite</button>
              </div>
            )}

            <div className="space-y-2">
              {partners.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold">
                      {p.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-700">{p.email}</p>
                      <p className="text-[10px] text-slate-400">Notifies on: {p.notifyOn.join(', ')}</p>
                    </div>
                  </div>
                  <button onClick={() => setPartners(partners.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex gap-3">
          {task && (
            <button
              onClick={() => { if (confirm("Archive task?")) { onSave({ id: task.id, status: 'ARCHIVED' }); onClose(); } }}
              className="p-3 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              <Archive size={18} />
            </button>
          )}
          <button onClick={onClose} className="flex-1 py-3 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
          <button
            onClick={() => onSave({ id: task?.id, title, currentQuadrant, description, status, category, subtasks, partners, dueDate, isCompleted: status === 'COMPLETED' })}
            className="flex-[2] py-3 bg-blue-600 text-white font-bold text-sm rounded-xl hover:bg-blue-700 transition-all shadow-sm"
          >
            Save Changes
          </button>
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={onUpgrade}
        feature={upgradeFeature}
      />
    </div>
  );
};
